import React from 'react';
import { FormField } from '@/components/FormField';
import { Seg } from './BaseAdSetForm';

const CLICK_WINDOWS = [
  { v: '1', l: '1 day' },
  { v: '7', l: '7 days' },
  { v: '28', l: '28 days' },
];

const VIEW_WINDOWS = [
  { v: '0', l: 'None' },
  { v: '1', l: '1 day' },
  { v: '7', l: '7 days' },
];

interface Props {
  clickWin: string;
  setClickWin: (v: string) => void;
  viewWin: string;
  setViewWin: (v: string) => void;
  /**
   * If true, only allow the click-through window — view-through is hidden.
   * Use for objectives where Meta doesn't credit view-through (e.g. AWARENESS,
   * REACH-only). Defaults to false (show both).
   */
  hideViewThrough?: boolean;
  /**
   * Restrict the click windows that can be picked. Meta hard-rejects windows
   * outside the objective's supported set (e.g. OUTCOME_TRAFFIC only accepts
   * 1 day — subcode 1885423), so don't offer what can't save.
   */
  clickOptions?: string[];
  /** Optional explanatory copy under the header. */
  hint?: string;
}

/**
 * Per-adset attribution window selector. Used inside the workspace's
 * objective-specific adset forms. The two values feed into Meta's
 * `attribution_spec` array via `buildAttributionSpec()`.
 *
 * Meta defaults (so the UI is correct out of the box):
 *   - TRAFFIC, ENGAGEMENT, APP_PROMOTION: 7d click, no view
 *   - LEADS, SALES, AWARENESS: 7d click + 1d view
 */
export const AttributionWindowSection: React.FC<Props> = ({
  clickWin,
  setClickWin,
  viewWin,
  setViewWin,
  hideViewThrough = false,
  hint,
  clickOptions,
}) => (
  <div className="rounded-xl border border-border bg-surface-alt/40 p-3 space-y-3">
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Attribution windows</div>
      {hint && <p className="mt-0.5 text-2xs text-text-muted">{hint}</p>}
    </div>
    <FormField label="Click-through window">
      <Seg
        options={clickOptions ? CLICK_WINDOWS.filter(w => clickOptions.includes(w.v)) : CLICK_WINDOWS}
        value={clickWin}
        onChange={setClickWin}
      />
    </FormField>
    {!hideViewThrough && (
      <FormField label="View-through window">
        <Seg options={VIEW_WINDOWS} value={viewWin} onChange={setViewWin} />
      </FormField>
    )}
  </div>
);

/**
 * Build Meta's `attribution_spec` array from the two selected windows.
 * View-through is omitted when `viewWin === '0'` (None).
 */
export function buildAttributionSpec(
  clickWin: string,
  viewWin: string,
): Array<{ event_type: string; window_days: number }> {
  const spec: Array<{ event_type: string; window_days: number }> = [
    { event_type: 'CLICK_THROUGH', window_days: parseInt(clickWin, 10) || 7 },
  ];
  if (viewWin && viewWin !== '0') {
    const v = parseInt(viewWin, 10);
    if (Number.isFinite(v) && v > 0) spec.push({ event_type: 'VIEW_THROUGH', window_days: v });
  }
  return spec;
}
